import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import {
  Botao,
  CheckboxUnform,
  DataInputUnform,
  FormRef,
  FormUnform,
  ModalBase,
  MoedaInputUnform
} from 'src/componentes'
import { GerarCobrancaRescisaoPayload } from 'src/servicos/api/rescisao'

import { AcoesModal, CorpoModal, TituloModal } from './styles'

interface DadosFormulario {
  valor: number
  dataVencimento: string
  permitirParcelamento?: boolean
}

export interface ModalGerarCobrancaRescisaoRef {
  abrir: (valorTotalAPagar?: number) => void
  fechar: () => void
}

interface ModalGerarCobrancaRescisaoProps {
  backdrop?: boolean
  acaoPrimaria: (dados: GerarCobrancaRescisaoPayload) => Promise<void>
}

const ModalGerarCobrancaRescisaoComponente: React.ForwardRefRenderFunction<
  ModalGerarCobrancaRescisaoRef,
  ModalGerarCobrancaRescisaoProps
> = ({ backdrop, acaoPrimaria }, ref) => {
  const formRef = useRef<FormRef>(null)
  const [aberta, definirAberta] = useState(false)
  const [carregando, definirCarregando] = useState(false)
  const [dadosIniciais, definirDadosIniciais] = useState<{
    valor?: number
    dataVencimento: string
    permitirParcelamento: boolean
  }>(null)
  // Sem essa key o form retém valores de aberturas anteriores do modal na mesma sessão
  // (mesmo fix já usado no modal de editar financeiro).
  const [chaveFormulario, definirChaveFormulario] = useState(0)

  const fechar = () => {
    definirAberta(false)
    definirCarregando(false)
    definirDadosIniciais(null)
  }

  const abrir = (valorTotalAPagar?: number) => {
    definirDadosIniciais({
      valor: valorTotalAPagar,
      dataVencimento: '',
      permitirParcelamento: false
    })
    definirChaveFormulario(chave => chave + 1)
    definirAberta(true)
  }

  useImperativeHandle(ref, () => ({ abrir, fechar }))

  const salvar = async (dados: DadosFormulario) => {
    // A forma de pagamento não é escolhida aqui — o aluno decide ao pagar no painel financeiro.
    // O DataInputUnform já entrega a data no formato ISO (YYYY-MM-DD) esperado pelo backend.
    const payload: GerarCobrancaRescisaoPayload = {
      valor: dados.valor,
      dataVencimento: dados.dataVencimento,
      permitirParcelamento: !!dados.permitirParcelamento
    }

    try {
      definirCarregando(true)
      await acaoPrimaria(payload)
      fechar()
    } finally {
      definirCarregando(false)
    }
  }

  return (
    <ModalBase
      id="modal-gerar-cobranca-rescisao"
      aberta={aberta}
      backdrop={backdrop}
    >
      <CorpoModal>
        <TituloModal>Gerar cobrança de rescisão</TituloModal>
        {dadosIniciais && (
          <FormUnform
            key={chaveFormulario}
            ref={formRef}
            dadosIniciais={dadosIniciais}
            acaoSucesso={salvar}
          >
            <MoedaInputUnform
              id="ipt_valor_cobranca_rescisao"
              name="valor"
              label="Valor da cobrança"
              obrigatorio
            />
            <DataInputUnform
              id="dt_vencimento_cobranca_rescisao"
              name="dataVencimento"
              label="Data de vencimento"
              obrigatorio
            />
            <CheckboxUnform
              id="chk_permitir_parcelamento_cobranca_rescisao"
              name="permitirParcelamento"
              texto="Permitir parcelamento em 12x"
            />
            <AcoesModal>
              <Botao
                texto="Cancelar"
                tema="Secundario"
                type="button"
                disabled={carregando}
                onClick={fechar}
              />
              <Botao texto="Gerar cobrança" type="submit" carregando={carregando} />
            </AcoesModal>
          </FormUnform>
        )}
      </CorpoModal>
    </ModalBase>
  )
}

export const ModalGerarCobrancaRescisao = forwardRef(
  ModalGerarCobrancaRescisaoComponente
)
